import { Injectable } from '@angular/core';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, last, map, switchMap, tap, toArray } from 'rxjs/operators';
import { AnimalDocumentCategory } from '../models/animal-document-category.enum';
import { AnimalMediaCategory } from '../models/animal-media-category.enum';
import {
  AnimalMedicalConditionCreatePayload,
  AnimalMedicalConditionUpdatePayload
} from '../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordCreatePayload } from '../models/medical/animal-medical-record.model';
import {
  AnimalPreventiveTreatmentCreatePayload,
  AnimalPreventiveTreatmentUpdatePayload
} from '../models/medical/animal-preventive-treatment.model';
import { AnimalQuarantineCreatePayload, AnimalQuarantineUpdatePayload } from '../models/medical/animal-quarantine.model';
import { AnimalDocumentsRepository } from '../states/animal-documents.repository';
import { AnimalImagesRepository } from '../states/animal-images.repository';
import { AnimalVideoRepository } from '../states/animal-video.repository';
import { AnimalMedicalConditionRepository } from '../states/medical/animal-medical-condition.repository';
import { AnimalMedicalRecordRepository } from '../states/medical/animal-medical-record.repository';
import { AnimalMedicalRepository } from '../states/medical/animal-medical.repository';
import { AnimalPreventiveTreatmentRepository } from '../states/medical/animal-preventive-treatment.repository';
import { AnimalQuarantineRepository } from '../states/medical/animal-quarantine.repository';

export type MedicalUploadTarget = 'document' | 'image' | 'video';

/**
 * Eine Position, die zusammen mit der Karteikarte gespeichert wird. `key` stammt aus dem
 * Formular, damit fehlgeschlagene Positionen im Modus „Nachtragen“ wiedergefunden werden.
 */
export type MedicalSaveOperation =
  | { kind: 'createTreatment'; key: string; payload: Omit<AnimalPreventiveTreatmentCreatePayload, 'medicalRecord'> }
  /** `relink`: Korrektur — die Position wird auf die neue Karteikarte umgehängt. */
  | { kind: 'updateTreatment'; key: string; id: number; payload: AnimalPreventiveTreatmentUpdatePayload; relink: boolean }
  | { kind: 'deleteTreatment'; key: string; id: number }
  | { kind: 'createCondition'; key: string; payload: Omit<AnimalMedicalConditionCreatePayload, 'diagnosedByRecord'> }
  | { kind: 'updateCondition'; key: string; id: number; payload: AnimalMedicalConditionUpdatePayload; relink: boolean }
  | { kind: 'deleteCondition'; key: string; id: number }
  | { kind: 'createQuarantine'; key: string; payload: AnimalQuarantineCreatePayload }
  | { kind: 'updateQuarantine'; key: string; id: number; payload: AnimalQuarantineUpdatePayload };

export interface MedicalUpload {
  key: string;
  file: File;
  target: MedicalUploadTarget;
  /** Nur bei Dokumenten: eine der medizinischen Kategorien. */
  category: AnimalDocumentCategory;
  /** `YYYY-MM-DD` */
  date: string;
  /** Video bereits hochgeladen, aber noch nicht verknüpft (Nachtragen nach Teilfehler). */
  uploadedVideoId?: number | null;
}

export interface MedicalSaveRequest {
  animalId: number;
  /** Neue Karteikarte; entfällt im Modus „Nachtragen“, dann ist `recordId` gesetzt. */
  record: AnimalMedicalRecordCreatePayload | null;
  recordId: number | null;
  operations: MedicalSaveOperation[];
  uploads: MedicalUpload[];
}

export interface MedicalSaveResult {
  /** `null`: Die Karteikarte selbst ist gescheitert, es wurde nichts gespeichert. */
  recordId: number | null;
  failedOperations: string[];
  failedUploads: string[];
  savedOperations: number;
  savedUploads: number;
}

/**
 * Orchestriert „Speichern zusammen“: Karteikarte, dann alle Positionen parallel, dann die
 * Uploads nacheinander, zuletzt Store und Tier neu laden. Das Backend kennt keinen
 * Sammel-Endpoint, daher meldet das Ergebnis Teilfehler je Position.
 */
@Injectable({ providedIn: 'root' })
export class MedicalRecordSaveService {
  constructor(
    private readonly _recordRepository: AnimalMedicalRecordRepository,
    private readonly _treatmentRepository: AnimalPreventiveTreatmentRepository,
    private readonly _conditionRepository: AnimalMedicalConditionRepository,
    private readonly _quarantineRepository: AnimalQuarantineRepository,
    private readonly _documentsRepository: AnimalDocumentsRepository,
    private readonly _imagesRepository: AnimalImagesRepository,
    private readonly _videoRepository: AnimalVideoRepository,
    private readonly _medicalRepository: AnimalMedicalRepository
  ) {}

  save(request: MedicalSaveRequest, onUploadProgress?: (done: number, total: number) => void): Observable<MedicalSaveResult> {
    const record$: Observable<number | null> = request.recordId !== null
      ? of(request.recordId)
      : request.record
        ? this._recordRepository.create(request.record, true).pipe(map(record => record?.id ?? null))
        : of(null);

    return record$.pipe(
      switchMap(recordId => {
        if (recordId === null) {
          return of<MedicalSaveResult>({
            recordId: null,
            failedOperations: request.operations.map(op => op.key),
            failedUploads: request.uploads.map(upload => upload.key),
            savedOperations: 0,
            savedUploads: 0,
          });
        }

        return this.runOperations(request.operations, recordId).pipe(
          switchMap(operationResults =>
            this.runUploads(request.uploads, request.animalId, recordId, onUploadProgress).pipe(
              map(uploadResults => ({
                recordId,
                failedOperations: operationResults.filter(r => !r.ok).map(r => r.key),
                failedUploads: uploadResults.filter(r => !r.ok).map(r => r.key),
                savedOperations: operationResults.filter(r => r.ok).length,
                savedUploads: uploadResults.filter(r => r.ok).length,
              }))
            )
          ),
          switchMap(result => this._medicalRepository.reload().pipe(
            catchError(() => of(undefined)),
            defaultIfEmpty(undefined),
            last(),
            map(() => result)
          ))
        );
      })
    );
  }

  private runOperations(operations: MedicalSaveOperation[], recordId: number): Observable<{ key: string; ok: boolean }[]> {
    if (!operations.length) return of([]);
    return forkJoin(operations.map(operation =>
      this.runOperation(operation, recordId).pipe(
        catchError(() => of(false)),
        map(ok => ({ key: operation.key, ok }))
      )
    ));
  }

  private runOperation(operation: MedicalSaveOperation, recordId: number): Observable<boolean> {
    const record = { id: recordId };
    switch (operation.kind) {
      case 'createTreatment':
        return this._treatmentRepository.create({ ...operation.payload, medicalRecord: record }, true).pipe(map(Boolean));
      case 'updateTreatment':
        return this._treatmentRepository.update(
          operation.id,
          operation.relink ? { ...operation.payload, medicalRecord: record } : { ...operation.payload, changedByRecord: record },
          true
        ).pipe(map(Boolean));
      case 'deleteTreatment':
        return this._treatmentRepository.delete(operation.id, true);
      case 'createCondition':
        return this._conditionRepository.create({ ...operation.payload, diagnosedByRecord: record }, true).pipe(map(Boolean));
      case 'updateCondition':
        return this._conditionRepository.update(
          operation.id,
          operation.relink ? { ...operation.payload, diagnosedByRecord: record } : { ...operation.payload, changedByRecord: record },
          true
        ).pipe(map(Boolean));
      case 'deleteCondition':
        return this._conditionRepository.delete(operation.id, true);
      case 'createQuarantine':
        return this._quarantineRepository.create(operation.payload, true).pipe(map(Boolean));
      case 'updateQuarantine':
        return this._quarantineRepository.update(operation.id, { ...operation.payload, changedByRecord: record }, true).pipe(map(Boolean));
    }
  }

  private runUploads(
    uploads: MedicalUpload[],
    animalId: number,
    recordId: number,
    onProgress?: (done: number, total: number) => void
  ): Observable<{ key: string; ok: boolean }[]> {
    if (!uploads.length) return of([]);
    let done = 0;
    onProgress?.(0, uploads.length);

    // Nacheinander, damit große Dateien sich nicht gegenseitig ausbremsen.
    return concat(...uploads.map(upload =>
      this.runUpload(upload, animalId, recordId).pipe(
        catchError(() => of(false)),
        map(ok => ({ key: upload.key, ok })),
        tap(() => onProgress?.(++done, uploads.length))
      )
    )).pipe(toArray());
  }

  private runUpload(upload: MedicalUpload, animalId: number, recordId: number): Observable<boolean> {
    if (upload.target === 'document') {
      const formData = this.baseFormData(upload, animalId);
      formData.append('category', upload.category);
      formData.append('medical_record_id', String(recordId));
      return this._documentsRepository.addDocument(formData, animalId).pipe(map(Boolean));
    }

    if (upload.target === 'image') {
      const formData = this.baseFormData(upload, animalId);
      formData.append('categories[]', AnimalMediaCategory.MEDICAL);
      formData.append('medical_record_id', String(recordId));
      return this._imagesRepository.addImage(formData, animalId).pipe(map(Boolean));
    }

    // Video: Upload kennt medical_record_id nicht → danach per PUT verknüpfen.
    const upload$: Observable<number | null> = upload.uploadedVideoId
      ? of(upload.uploadedVideoId)
      : (() => {
          const formData = this.baseFormData(upload, animalId);
          formData.append('categories[]', AnimalMediaCategory.MEDICAL);
          return this._videoRepository.addVideo(formData, animalId).pipe(map(video => video?.id ?? null));
        })();

    return upload$.pipe(
      switchMap(videoId => {
        if (!videoId) return of(false);
        upload.uploadedVideoId = videoId;
        return this._videoRepository.linkToMedicalRecord(videoId, recordId, animalId).pipe(map(Boolean));
      })
    );
  }

  private baseFormData(upload: MedicalUpload, animalId: number): FormData {
    const formData = new FormData();
    formData.append('file', upload.file);
    formData.append('animal_id', String(animalId));
    formData.append('date', upload.date);
    return formData;
  }
}
